<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { ref } from 'vue'

import CloseIcon from '../../assets/icons/close.svg?component'

withDefaults(
  defineProps<{
    placeholder?: string
    maxHashtagLength?: number
    disabled?: boolean
    name?: string
  }>(),
  {
    placeholder: 'Enter hashtags, comma separated',
    maxHashtagLength: 255,
    disabled: false,
    name: undefined,
  },
)

const hashtags = defineModel<string[]>({ default: () => [] })

const inputText = ref('')
const inputRef = ref<HTMLInputElement>()

const graphemeSegmenter = new Intl.Segmenter()
const wordChar = /[\p{L}\p{N}_]/u
const emoji = /[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}]/u

const sanitize = (raw: string, maxLength: number): string => {
  const graphemes = Array.from(graphemeSegmenter.segment(raw), (segment) => segment.segment)
  const kept = graphemes.filter((grapheme) => wordChar.test(grapheme) || emoji.test(grapheme))
  return kept.slice(0, maxLength).join('')
}

const addTokens = (raw: string, maxLength: number) => {
  const next = [...hashtags.value]
  const seen = new Set(next.map((tag) => tag.toLowerCase()))

  raw.split(/[\s,]+/).forEach((token) => {
    const clean = sanitize(token, maxLength)
    if (!clean || seen.has(clean.toLowerCase())) {
      return
    }
    seen.add(clean.toLowerCase())
    next.push(clean)
  })

  if (next.length !== hashtags.value.length) {
    hashtags.value = next
  }
}

const onInput = (event: Event, maxLength: number) => {
  const value = (event.target as HTMLInputElement).value

  if (/[\s,]/.test(value)) {
    addTokens(value, maxLength)
    inputText.value = ''
  } else {
    inputText.value = value
  }
}

const commit = (maxLength: number) => {
  addTokens(inputText.value, maxLength)
  inputText.value = ''
}

const removeAt = (index: number) => {
  hashtags.value = hashtags.value.filter((_, i) => i !== index)
}

const onBackspace = () => {
  if (inputText.value === '' && hashtags.value.length) {
    removeAt(hashtags.value.length - 1)
  }
}
</script>
<template>
  <div :class="['hashtags-input', { 'hashtags-input--disabled': disabled }]" data-test="hashtagsInput" @click="inputRef?.focus()">
    <span v-for="(hashtag, index) in hashtags" :key="hashtag" class="hashtags-input__chip" data-test="hashtagChip">
      <span class="hashtags-input__chip-label">#{{ hashtag }}</span>
      <button
        v-if="!disabled"
        type="button"
        class="hashtags-input__chip-remove"
        data-test="hashtagChipRemove"
        aria-label="Remove hashtag"
        @click.stop="removeAt(index)"
      >
        <ElIcon :size="10">
          <CloseIcon />
        </ElIcon>
      </button>
    </span>
    <input
      ref="inputRef"
      :value="inputText"
      :name="name"
      :disabled="disabled"
      :placeholder="hashtags.length ? '' : placeholder"
      class="hashtags-input__field"
      type="text"
      @input="onInput($event, maxHashtagLength)"
      @keydown.enter.prevent="commit(maxHashtagLength)"
      @keydown.delete="onBackspace"
      @blur="commit(maxHashtagLength)"
    />
  </div>
</template>
<style scoped lang="scss">
.hashtags-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 56px;
  padding: 8px 16px;
  border: 1px solid rgba(34, 34, 34, 0.12);
  border-radius: 8px;
  background: var(--inverted);
  cursor: text;
  &:focus-within {
    border-color: rgba(34, 34, 34, 0.64);
  }
  &--disabled {
    cursor: not-allowed;
    background: rgba(34, 34, 34, 0.02);
  }
}
.hashtags-input__chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 8px;
  background: rgba(34, 34, 34, 0.08);
  color: var(--primary);
  font-size: 14px;
  line-height: 20px;
}
.hashtags-input__chip-remove {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  path {
    fill: var(--primary);
  }
}
.hashtags-input__field {
  flex: 1 0 120px;
  min-width: 120px;
  height: 32px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--primary);
  font-size: 16px;
  line-height: 24px;
  &::placeholder {
    color: rgba(34, 34, 34, 0.24);
  }
}
</style>
